import React, { useMemo, useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { useLocation } from '@reach/router'
import { getURIParamValue } from 'src/modules/utils/analytics'
import { isValidVideoSrc } from 'src/modules/utils/media'
import { LogoSyncContext } from 'src/components/common/AnnotationContext'
import { getImageName } from 'src/modules/utils/files'
import { imgIX } from 'src/modules/utils/imgIX'
import useIsMobile from 'src/modules/hooks/useIsMobile'
import Image from '../../Image'
import { colors } from 'src/constants/marketing-ds'

const baseVideoSrcPath = 'https://assets.fubo.tv/marketing/lp/videos/compressed'
const baseVideoMobileSrcPath = 'https://assets.fubo.tv/marketing/lp/videos/mobile'
const baseVideoFrameSrcPath = 'https://assets-imgx.fubo.tv/marketing/lp/videos/firstframe'
// const posterImgDefault = "https://assets-imgx.fubo.tv/welcome/us/june_2021/us_june_ff.png?auto=format,compress"
// const posterImgDefault = "src/assets/images/poster/black-pixel.png"

const HeroVideo = ({
  heroBgVideo,
  heroBgVideoMobile,
  posterImg,
  posterImgMobile,
  containVideo = false,
  noVideo,
}) => {
  const location = useLocation() || (typeof window !== 'undefined' && window.location)
  const isMobile = useIsMobile(768)
  const videoRef = useRef(null)
  const videoObserver = useRef()
  const contextValue = React.useContext(LogoSyncContext)
  const newBgVideoURI = getURIParamValue(location.search, 'bgvideo')

  // Memoize utility functions
  const parseVideoName = useCallback((url) => {
    const parts = url.split('/')
    return parts[parts.length - 1]
  }, [])

  const getVideoParamSrc = useCallback(() => {
    if (isValidVideoSrc(newBgVideoURI)) {
      return `${baseVideoSrcPath}/${newBgVideoURI}`
    }
    return false
  }, [newBgVideoURI])

  const checkVideoMobile = useCallback(
    (videoUrl) => {
      if (!isMobile) return videoUrl
      return (
        getVideoParamSrc() ||
        heroBgVideoMobile ||
        baseVideoMobileSrcPath + '/' + parseVideoName(videoUrl)
      )
    },
    [isMobile, getVideoParamSrc, heroBgVideoMobile, parseVideoName]
  )

  const getFirstFrame = useCallback(
    (videoSrc) => {
      if (!videoSrc) return undefined

      let poster = baseVideoFrameSrcPath + '/' + getImageName(videoSrc)
      if (isMobile) {
        poster += '?ar=1:1&fit=crop'
      } else {
        poster += '?w=2048&ch=width&auto=format,compress'
      }
      return poster
    },
    [isMobile]
  )

  // Memoize video source calculation
  const videoSrc = useMemo(() => {
    if (isValidVideoSrc(newBgVideoURI)) {
      const src = `${baseVideoSrcPath}/${newBgVideoURI}`
      return checkVideoMobile(src)
    }
    return heroBgVideo ? checkVideoMobile(heroBgVideo) : ''
  }, [newBgVideoURI, heroBgVideo, checkVideoMobile])

  // Memoize poster image calculation
  const posterSrc = useMemo(() => {
    if (isValidVideoSrc(newBgVideoURI)) {
      const src = `${baseVideoSrcPath}/${newBgVideoURI}`
      return getFirstFrame(src)
    }

    if (heroBgVideo) {
      return getFirstFrame(heroBgVideo)
    }

    const posterImgCheck = isMobile && posterImgMobile ? posterImgMobile : posterImg
    // Optimize imgIX parameters for faster loading
    const imgIXParams = {
      isCompress: true,
      w: isMobile ? 800 : 2048,
    }

    return imgIX(posterImgCheck, imgIXParams)
  }, [newBgVideoURI, isMobile, posterImg, posterImgMobile, heroBgVideo])

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current
      video.ontimeupdate = (event) => {
        contextValue.onTimeUpdate(video.currentTime)
      }
    }
  }, [videoSrc])

  useEffect(() => {
    if ('IntersectionObserver' in window) {
      videoObserver.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const video = entry.target
            const videoSource = video.querySelector('source')
            if (videoSource && videoSource.dataset.src) {
              videoSource.src = videoSource.dataset.src
              video.load()
              video.classList.remove('lazy')
              videoObserver.current.unobserve(video)
            }
          }
        })
      })

      const videoEl = videoRef.current
      if (videoEl) {
        videoObserver.current.observe(videoEl)
      }

      return () => {
        if (videoEl) {
          videoObserver.current.unobserve(videoEl)
        }
      }
    }
  }, [videoSrc])

  return (
    <VideoWrapper className="videoWrapper">
      <HeroImageContainer>
        {posterSrc && (
          <HeroImage
            src={posterSrc}
            alt="Hero Poster"
            isCompress={false}
            width="100%"
            height="100%"
            loading="eager"
            fetchPriority="high"
          />
        )}
      </HeroImageContainer>
      <VideoElement
        className={containVideo ? 'containVideo lazy' : 'coverVideo lazy'}
        ref={videoRef}
        // change to a var that lets me switch between dekstop and mobile
        key={!noVideo && videoSrc}
        // change to a var that lets me switch between dekstop and mobile, so that the image is imgx cropped
        // poster={statePosterImg}
        loop
        muted
        autoPlay
        playsInline
      >
        {!noVideo && <source data-src={videoSrc} type="video/mp4" />}
      </VideoElement>
    </VideoWrapper>
  )
}

export default HeroVideo

const VideoWrapper = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  box-sizing: border-box;
  z-index: 2;
  background: ${colors.gradients.midnight.medium};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`

const HeroImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${colors.gradients.midnight.medium};
  min-height: 200px;
`

const HeroImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const VideoElement = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  transform: translate(-50%, -50%);
  object-fit: cover;

  &.containVideo {
    position: relative;
    top: 0;
    left: 0;
    transform: none;
    object-fit: contain;
  }
`
